import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {object} Refs
 * @property {HTMLElement} content – The container for recommendations content.
 */

/**
 * A custom element that loads and displays product recommendations in the cart drawer.
 *
 * @extends Component<Refs>
 */
class CartDrawerRecommendations extends Component {
  /** @type {Record<string, Promise<Response>>} */
  static recommendationsCache = {};
  /** @type {(event: Event) => void} */
  _cartUpdateHandler = this.#handleCartUpdate.bind(this);
  /** @type {string | null} */
  #savedManualContent = null;

  async connectedCallback() {
    super.connectedCallback();

    // Ajouter l'attribut pour empêcher le morph de remplacer ce block
    // Cela évite le clignotement lors des mises à jour du panier
    this.setAttribute('data-skip-subtree-update', '');

    // Initialiser le previousProductId et previousManualMode avec les valeurs actuelles
    this.#previousProductId = this.getAttribute('product-id');
    this.#previousManualMode = this.hasAttribute('data-manual-mode');

    // Sauvegarder le contenu manuel initial s'il existe (pour pouvoir le restaurer plus tard)
    // C'est nécessaire car la Section Rendering API ne peut pas accéder aux configurations
    // des blocs statiques dans les section groups
    if (this.hasAttribute('data-manual-mode') && this.refs.content && this.refs.content.hasChildNodes()) {
      this.#savedManualContent = this.refs.content.innerHTML;
    }

    // Écouter les mises à jour du panier pour recharger les recommandations
    document.addEventListener(ThemeEvents.cartUpdate, this._cartUpdateHandler);

    await this.#loadRecommendations();

    // Réinitialiser le slider après le chargement initial
    this.#reInitSlider();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this._cartUpdateHandler);
  }

  /**
   * Charge les recommandations de produits
   * @param {boolean} [forceReload=false] - Force le rechargement même si le contenu existe déjà
   */
  async #loadRecommendations(forceReload = false) {
    const productId = this.getAttribute('product-id');
    const limit = this.getAttribute('limit') || 4;
    const sectionId = 'cart-drawer-recommendations';
    const isManualMode = this.hasAttribute('data-manual-mode');

    // En mode manuel, ne pas charger depuis l'API - le contenu est déjà rendu côté serveur
    if (isManualMode) {
      if (this.refs.content && this.refs.content.hasChildNodes()) {
        this.hidden = false;
        this.#reInitSlider();
      }
      return;
    }

    if (!productId || productId === 'manual') {
      // Ne cacher que si vraiment nécessaire (panier vide et pas de mode manuel)
      // Ne pas cacher pendant le rechargement pour éviter le clignotement
      if (!this.refs.content || !this.refs.content.hasChildNodes()) {
        this.hidden = true;
      }
      return;
    }

    // Check if content already exists (server-rendered) - sauf si on force le rechargement
    if (!forceReload && this.refs.content && this.refs.content.hasChildNodes() && this.getAttribute('product-id') === productId) {
      // Si le contenu existe déjà, réinitialiser le slider
      this.#reInitSlider();
      return;
    }

    // Ne pas cacher pendant le chargement pour éviter le clignotement
    // Le contenu existant restera visible jusqu'à ce que le nouveau soit prêt
    const hasExistingContent = this.refs.content && this.refs.content.hasChildNodes();

    if (!CartDrawerRecommendations.recommendationsCache[productId]) {
      const recommendationsUrl = `${Theme.routes.recommendations_url}?product_id=${productId}&limit=${limit}&section_id=${sectionId}&intent=related`;
      CartDrawerRecommendations.recommendationsCache[productId] = fetch(recommendationsUrl);
    }

    try {
      const response = await CartDrawerRecommendations.recommendationsCache[productId];
      const html = await response.clone().text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const productRecommendationsElement = doc.querySelector('cart-drawer-recommendations');

      if (productRecommendationsElement && productRecommendationsElement.hasChildNodes()) {
        // Use morph to update content if ref exists for smooth transition
        if (this.refs.content) {
          const newContent = productRecommendationsElement.querySelector('[ref="content"]') || productRecommendationsElement;
          // Le morph préserve le contenu existant et le met à jour de manière fluide
          morph(this.refs.content, newContent);
          // Réinitialiser le slider après le morph
          this.#reInitSlider();
        } else {
          this.innerHTML = productRecommendationsElement.innerHTML;
          this.#reInitSlider();
        }
        this.hidden = false;
      } else {
        // Seulement cacher si on n'avait pas de contenu avant
        if (!hasExistingContent) {
          this.hidden = true;
        }
      }
    } catch (error) {
      console.error('Error loading cart drawer recommendations:', error);
      // Seulement cacher si on n'avait pas de contenu avant
      if (!hasExistingContent) {
        this.hidden = true;
      }
    }
  }

  /**
   * Gère les mises à jour du panier
   * @param {Event} event
   */
  async #handleCartUpdate(event) {
    // Attendre que le morph de la section soit terminé pour récupérer le nouveau product-id
    // Le morph met à jour l'attribut product-id sur l'élément même avec data-skip-subtree-update
    // Utiliser requestAnimationFrame pour s'assurer que le DOM est à jour
    await new Promise((/** @type {(value?: void) => void} */ resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          resolve();
        });
      });
    });

    // Récupérer le nouveau product-id depuis l'attribut
    // Avec data-skip-subtree-update, le morph met toujours à jour les attributs de l'élément
    // mais ne remplace pas les enfants, ce qui évite le clignotement
    const newProductId = this.getAttribute('product-id');
    const oldProductId = this.#previousProductId;
    const isManualMode = this.hasAttribute('data-manual-mode');
    const wasManualMode = this.#previousManualMode;

    // Détecter si on vient de passer en mode manuel (panier vide)
    if (isManualMode && !wasManualMode) {
      this.#previousManualMode = true;
      this.#previousProductId = newProductId;
      // Restaurer le contenu manuel sauvegardé
      await this.#loadManualContent();
      return;
    }

    // Détecter si on quitte le mode manuel (panier non-vide)
    if (!isManualMode && wasManualMode) {
      this.#previousManualMode = false;
    }

    // En mode manuel avec contenu déjà présent, juste s'assurer que c'est visible
    if (isManualMode) {
      if (this.refs.content && this.refs.content.hasChildNodes()) {
        this.hidden = false;
        this.#reInitSlider();
      }
      return;
    }

    // Si le product-id a changé, mettre à jour et recharger
    if (newProductId !== oldProductId) {
      this.#previousProductId = newProductId;

      if (!newProductId || newProductId === 'manual') {
        // Si le panier est vide ou en mode manuel, cacher les recommandations seulement si elles existent
        // Ne pas créer de clignotement si elles sont déjà cachées
        if (this.refs.content && this.refs.content.hasChildNodes()) {
          this.hidden = true;
        }
        return;
      }

      // Invalider le cache pour forcer le rechargement avec le nouveau product-id
      if (oldProductId && CartDrawerRecommendations.recommendationsCache[oldProductId]) {
        delete CartDrawerRecommendations.recommendationsCache[oldProductId];
      }

      await this.#loadRecommendations(true);
    } else if (newProductId) {
      // Même product-id, mais le contenu du panier a changé
      // Il faut recharger pour exclure les produits déjà dans le panier
      // Invalider le cache pour forcer le rechargement
      if (CartDrawerRecommendations.recommendationsCache[newProductId]) {
        delete CartDrawerRecommendations.recommendationsCache[newProductId];
      }
      await this.#loadRecommendations(true);
    } else {
      // Pas de product-id, cacher les recommandations seulement si elles existent
      if (this.refs.content && this.refs.content.hasChildNodes()) {
        this.hidden = true;
      }
    }
  }

  /**
   * Restaure le contenu manuel sauvegardé lors du chargement initial
   * Cette approche est nécessaire car la Section Rendering API ne peut pas
   * accéder aux configurations des blocs statiques dans les section groups
   */
  async #loadManualContent() {
    if (!this.#savedManualContent) {
      return;
    }

    if (!this.refs.content) {
      return;
    }

    // Créer un élément temporaire avec le contenu sauvegardé pour le morph
    const tempDiv = document.createElement('div');
    tempDiv.setAttribute('ref', 'content');
    tempDiv.innerHTML = this.#savedManualContent;

    // Utiliser morph pour une transition fluide
    morph(this.refs.content, tempDiv);
    this.hidden = false;
    this.#reInitSlider();
  }

  /** @type {string | null} */
  #previousProductId = null;

  /** @type {boolean} */
  #previousManualMode = false;

  /**
   * Réinitialise le slider après un morph ou un chargement
   */
  #reInitSlider() {
    // Trouver le slider-component dans le contenu
    const slider = /** @type {any} */ (this.querySelector('slider-component'));
    if (slider && typeof slider.reInit === 'function') {
      slider.reInit();
    }
  }

  get productId() {
    return this.getAttribute('product-id');
  }
}

if (!customElements.get('cart-drawer-recommendations')) {
  customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
}
